import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, MailPlus, Search, Loader2, Pencil, Ticket, Crown, Wand2, UploadCloud, Check, X, FlaskConical, Wrench, ChevronDown, Clock, ShieldCheck, MessageCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ReservaFormDialog from "@/components/reservas/ReservaFormDialog";
import IngestEmailDialog from "@/components/reservas/IngestEmailDialog";
import ImportarReservasDialog from "@/components/reservas/ImportarReservasDialog";
import PendientesVencidasDialog from "@/components/reservas/PendientesVencidasDialog";
import { LimpiezaDatosDialog } from "@/components/reservas/LimpiezaDatosDialog";
import { AsignarHorarioDialog } from "@/components/reservas/AsignarHorarioDialog";
import { CuposDesincronizadosDialog } from "@/components/reservas/CuposDesincronizadosDialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ESTADOS_RESERVA, CANALES, TIPOS_MODALIDAD, VARIANTE_NIAGARA, paxResumen, idiomaAbbr } from "@/lib/constants";
import { formatFechaCorta } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

function AgenciaCell({ reserva }) {
  if (!reserva.agencia) {
    return reserva.requiere_asignacion_agencia ? (
      <span className="text-[11px] text-amber-600">Sin asignar</span>
    ) : (
      <span className="text-slate-300">—</span>
    );
  }
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-slate-700">{reserva.agencia}</span>
      {reserva.facturada && (
        <>
          <span className="inline-flex w-fit items-center px-1.5 py-0.5 mt-0.5 rounded text-[10px] font-medium border bg-violet-50 text-violet-700 border-violet-200">
            Facturada
          </span>
          {Number(reserva.monto_facturado_agencia || 0) > 0 && (
            <span className="text-[10px] text-slate-400 mt-0.5">
              ${Number(reserva.monto_facturado_agencia).toFixed(2)} facturado
            </span>
          )}
        </>
      )}
    </div>
  );
}

function EstadoBadge({ estado }) {
  const e = ESTADOS_RESERVA[estado] || ESTADOS_RESERVA.pendiente;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${e.cls}`}>
      {e.label}
    </span>
  );
}

function IdiomaBadge({ idioma }) {
  const abr = idiomaAbbr(idioma);
  return (
    <span
      className={`inline-flex items-center justify-center px-1 py-0 rounded text-[9px] font-bold border leading-4 ${
        abr === "EN" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"
      }`}
      title={abr === "EN" ? "Inglés" : "Español"}
      data-testid={`idioma-badge-${abr.toLowerCase()}`}
    >
      {abr}
    </span>
  );
}

function CanalBadge({ sources = [] }) {
  const canal = sources[0]?.canal || "manual";
  const cuenta = sources[0]?.cuenta;
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-slate-700">{CANALES[canal] || canal}</span>
      {cuenta && <span className="text-[10px] text-slate-400">{cuenta}</span>}
    </div>
  );
}

function VarianteBadge({ variante }) {
  const v = VARIANTE_NIAGARA[variante];
  if (!v) return null;
  return (
    <span
      className={`inline-flex items-center mt-0.5 ml-1 px-1.5 py-0 rounded text-[10px] font-medium border ${v.cls}`}
      data-testid={`variante-badge-${variante}`}
    >
      {v.label}
    </span>
  );
}

function SaldoCell({ reserva }) {
  const saldo = Number(reserva.saldo_pendiente || 0);
  const moneda = reserva.moneda && reserva.moneda !== "USD" ? ` ${reserva.moneda}` : "";
  if (!saldo) {
    return (
      <span className="text-xs text-slate-400" data-testid={`reserva-saldo-${reserva.id}`}>
        {reserva.facturada ? "Facturada" : "$0.00"}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap"
      data-testid={`reserva-saldo-${reserva.id}`}
      title="Saldo pendiente de cobro"
    >
      ${saldo.toFixed(2)}{moneda}
    </span>
  );
}

function TablaReservas({ rows, tours, loading, onEdit, onDecidir, decidiendoId, isAdmin, onTogglePrueba, togglingId }) {
const navigate = useNavigate(); // PROMPT 147: el boton de WhatsApp lleva al chat de esa reserva
  const nombreTour = (r) => {
    const base = r.tour_nombre || tours.find((t) => t.id === r.tour_id)?.nombre || r.producto_raw || "—";
    return nombreTourDisplay(base, r.modalidad);
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Código", "Pasajero", "Tour · Modalidad", "Fecha · Hora", "Pax", "Saldo", "Estado", "Agencia", "Canal", ""].map((h, i) => (
                <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="h-32 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-32 text-center text-slate-400 text-sm">
                  No hay reservas que coincidan.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`reserva-row-${r.id}`} className={`hover:bg-slate-50 ${r.es_prueba ? "bg-amber-50/40" : ""}`}>
                  <TableCell className="font-mono text-xs text-slate-600">
                    <div className="flex flex-col" data-testid={`reserva-codigos-${r.id}`}>
                      <span data-testid={`reserva-codigo-booking-${r.id}`}>{r.codigo_booking}</span>
                      {r.es_prueba && (
                        <span className="inline-flex w-fit items-center gap-1 mt-0.5 px-1.5 py-0 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200">
                          <FlaskConical className="h-2.5 w-2.5" /> Prueba
                        </span>
                      )}
                      {r.referencia_externa ? (
                        <span
                          className="text-slate-400"
                          title="Referencia del reseller/OTA (la que aparece en el correo del cliente y sirve para buscar la reserva)"
                          data-testid={`reserva-ref-externa-${r.id}`}
                        >
                          Ref: {r.referencia_externa}
                        </span>
                      ) : (
                        r.codigo_pago && (
                          <span className="text-slate-400" data-testid={`reserva-codigo-pago-${r.id}`}>
                            Pago: {r.codigo_pago}
                          </span>
                        )
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    <div className="flex items-center gap-1.5">
                      <span>{r.pasajero?.nombre} {r.pasajero?.apellido}</span>
                      <IdiomaBadge idioma={r.pasajero?.idioma} />
                    </div>
                    {r.pasajero?.telefono && (
                      <div className="text-[11px] text-slate-400 font-normal">{r.pasajero.telefono}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[220px]">
                    <div className="truncate" title={nombreTour(r)}>{nombreTour(r)}</div>
                    <span className="inline-flex items-center mt-0.5 px-1.5 py-0 rounded text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
                      {TIPOS_MODALIDAD[r.modalidad] || r.modalidad}
                    </span>
                    <VarianteBadge variante={r.variante} />
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="font-mono text-slate-700">{formatFechaCorta(r.fecha)}</span>
                    <span className="text-slate-400"> · {r.hora || "—"}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{paxResumen(r)}</TableCell>
                  <TableCell><SaldoCell reserva={r} /></TableCell>
                  <TableCell><EstadoBadge estado={r.estado} /></TableCell>
                  <TableCell><AgenciaCell reserva={r} /></TableCell>
                  <TableCell><CanalBadge sources={r.sources} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {onDecidir && r.estado === "pendiente" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={decidiendoId === r.id}
                            onClick={() => onDecidir(r, "rechazada")}
                            data-testid={`rechazar-privado-${r.id}`}
                            className="h-8 gap-1 text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                          >
                            <X className="h-3.5 w-3.5" /> Rechazar
                          </Button>
                          <Button
                            size="sm"
                            disabled={decidiendoId === r.id}
                            onClick={() => onDecidir(r, "confirmada")}
                            data-testid={`aceptar-privado-${r.id}`}
                            className="h-8 gap-1"
                          >
                            {decidiendoId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Aceptar
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(r)}
                        data-testid={`edit-reserva-${r.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-slate-900"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/agente?reserva=${encodeURIComponent(r.id)}`)}
                          data-testid={`wa-reserva-${r.id}`}
                          title="Escribirle por WhatsApp"
                          className="h-8 w-8 text-emerald-600 hover:text-emerald-800"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      {isAdmin && onTogglePrueba && (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={togglingId === r.id}
                          onClick={() => onTogglePrueba(r)}
                          data-testid={`toggle-prueba-${r.id}`}
                          title={r.es_prueba ? "Quitar de reservas de prueba" : "Marcar como reserva de prueba (se oculta de la lista real)"}
                          className={`h-8 w-8 ${r.es_prueba ? "text-amber-600 hover:text-amber-700" : "text-slate-400 hover:text-amber-600"}`}
                        >
                          {togglingId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export default function ReservasPage() {
  const { isAdmin } = useAuth();
  const [reservas, setReservas] = useState([]);
  const [tours, setTours] = useState([]);
  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  // PROMPT 149: si la direccion trae ?q=<texto> (la ficha del cliente), el buscador arranca con ese texto.
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [estado, setEstado] = useState("todos");
  const [canal, setCanal] = useState("todos");
  const [tourFilter, setTourFilter] = useState("todos");
  const [horaFilter, setHoraFilter] = useState("todos");
  const [modalidadFilter, setModalidadFilter] = useState("todos");
  const [agenciaFilter, setAgenciaFilter] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [mostrarPrueba, setMostrarPrueba] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [tab, setTab] = useState("regular");
  const [formOpen, setFormOpen] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [vencidasOpen, setVencidasOpen] = useState(false);
  const [limpiezaOpen, setLimpiezaOpen] = useState(false);
  const [sinHorarioOpen, setSinHorarioOpen] = useState(false);
  const [cuposOpen, setCuposOpen] = useState(false);
  const [sinHorarioCount, setSinHorarioCount] = useState(0);
  // PROMPT 109: cuantas reservas coinciden de verdad (la lista trae como mucho 2000).
  const [totalReservas, setTotalReservas] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (estado !== "todos") params.estado = estado;
      if (canal !== "todos") params.canal = canal;
      if (tourFilter !== "todos") params.tour_id = tourFilter;
      if (modalidadFilter !== "todos") params.modalidad = modalidadFilter;
      if (agenciaFilter !== "todos") params.agencia_id = agenciaFilter;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (mostrarPrueba) params.incluir_prueba = true;
      const res = await api.get("/reservas", { params });
      const data = res.data;
      setReservas(data);
      // PROMPT 109: el total real viene en la cabecera X-Total-Count.
      const totalCabecera = parseInt(res.headers?.["x-total-count"], 10);
      setTotalReservas(Number.isFinite(totalCabecera) ? totalCabecera : null);
      // PROMPT 108: Reservas no muestra las que esperan revision (esas viven en la Bandeja).
      // Si la busqueda no encuentra nada aca, se mira si esta alla y se avisa donde esta.
      if (q && q.trim().length >= 4 && data.length === 0) {
        api.get("/reservas/bandeja", { params: { q: q.trim() } })
          .then(({ data: enBandeja }) => {
            const r = (enBandeja || [])[0];
            if (r) {
              toast.info(
                `${r.codigo_booking} no aparece acá porque está en la Bandeja, en "A revisar"` +
                  (r.motivo_revision ? ` (${r.motivo_revision})` : "") + ". Buscala ahí para completarla.",
                { id: "reserva-en-bandeja", duration: 12000 });
            }
          })
          .catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, estado, canal, tourFilter, modalidadFilter, agenciaFilter, desde, hasta, mostrarPrueba]);

  useEffect(() => {
    api.get("/tours", { params: { activo: true } }).then(({ data }) => setTours(data)).catch(() => {});
    api.get("/agencias").then(({ data }) => setAgencias(data)).catch(() => {});
  }, []);

  const loadSinHorario = useCallback(() => {
    api.get("/reservas/sin-horario")
      .then(({ data }) => setSinHorarioCount((data || []).length))
      .catch(() => {});
  }, []);

  useEffect(() => { loadSinHorario(); }, [loadSinHorario]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const regulares = reservas.filter((r) => (r.tipo || "regular") !== "privado");
  const privados = reservas.filter((r) => r.tipo === "privado");
  // Horas disponibles calculadas SOBRE el resultado ya filtrado por los demás filtros
  // (tour/estado/etc.), para que la lista de horas tenga sentido con lo que se está
  // mirando (ej. filtrar por "Washington" y ver solo sus horarios reales: 5:00, 6:00...).
  const horasDisponibles = Array.from(new Set(reservas.map((r) => r.hora).filter(Boolean))).sort();
  const regularesPorHora = horaFilter === "todos" ? regulares : regulares.filter((r) => r.hora === horaFilter);
  const privadosPorHora = horaFilter === "todos" ? privados : privados.filter((r) => r.hora === horaFilter);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setFormOpen(true);
  };

  const [reclasificando, setReclasificando] = useState(false);
  const [decidiendoId, setDecidiendoId] = useState(null);
  const decidirPrivado = async (r, nuevoEstado) => {
    setDecidiendoId(r.id);
    try {
      await api.post(`/reservas/${r.id}/estado`, { nuevo_estado: nuevoEstado });
      toast.success(nuevoEstado === "confirmada" ? "Privado aceptado y confirmado." : "Solicitud de privado rechazada.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDecidiendoId(null);
    }
  };
  const reclasificarVip = async () => {
    setReclasificando(true);
    try {
      const { data } = await api.post("/reservas/reclasificar-modalidad");
      if (data.reclasificadas > 0) {
        toast.success(`${data.reclasificadas} reserva(s) reclasificada(s) de Privado a VIP.`);
      } else {
        toast.info("No se encontraron reservas mal clasificadas como privado. ¡Todo en orden!");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReclasificando(false);
    }
  };

  const [reprocesandoBokun, setReprocesandoBokun] = useState(false);
  const reprocesarMapeoBokun = async () => {
    setReprocesandoBokun(true);
    try {
      const { data } = await api.post("/reservas/reprocesar-mapeo-bokun");
      if (data.resueltas > 0) {
        toast.success(`${data.resueltas} reserva(s) de Bókun/Viator mapeadas y confirmadas (de ${data.revisadas} en Bandeja).`);
      } else {
        toast.info(`Se revisaron ${data.revisadas} reserva(s) sin tour de Bókun/Viator y ninguna pudo mapearse automáticamente.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReprocesandoBokun(false);
    }
  };

  const [corrigiendoVipAgencia, setCorrigiendoVipAgencia] = useState(false);
  const corregirTourAgenciaVip = async () => {
    setCorrigiendoVipAgencia(true);
    try {
      const { data } = await api.post("/reservas/corregir-tour-agencia-vip");
      if (data.corregidas > 0) {
        toast.success(`${data.corregidas} reserva(s) corregida(s): el nombre de la agencia había hecho que se mapearan a un tour "... VIP" por error.`);
      } else {
        toast.info(`Se revisaron ${data.revisadas} reserva(s) en tours "... VIP" y ninguna tenía el error de mapeo por nombre de agencia.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCorrigiendoVipAgencia(false);
    }
  };

  const [corrigiendoCancelaciones, setCorrigiendoCancelaciones] = useState(false);
  const corregirCancelacionesDesfasadas = async () => {
    setCorrigiendoCancelaciones(true);
    try {
      const { data } = await api.post("/reservas/corregir-cancelaciones-desfasadas");
      if (data.corregidas > 0) {
        toast.success(`${data.corregidas} reserva(s) corregida(s): tenían la cancelación/reprogramación "aplicada" en el registro, pero nunca cambiaron de estado.`);
      } else {
        toast.info(`Se revisaron ${data.revisadas} solicitud(es) "aplicada(s)" y todas coinciden con el estado real de la reserva. Sin desfasajes.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCorrigiendoCancelaciones(false);
    }
  };

  const [limpiandoDuplicados, setLimpiandoDuplicados] = useState(false);
  const limpiarDuplicadosCargaMasiva = async () => {
    setLimpiandoDuplicados(true);
    try {
      const { data } = await api.post("/reservas/limpiar-duplicados-carga-masiva");
      if (data.eliminadas > 0) {
        toast.success(`${data.eliminadas} reserva(s) duplicada(s) de carga masiva eliminada(s) (se conservó la más antigua de cada par).`);
      } else {
        toast.info("No se encontraron duplicados de carga masiva. ¡Todo en orden!");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLimpiandoDuplicados(false);
    }
  };

  const [diagnosticando, setDiagnosticando] = useState(false);
  const diagnosticoBarcoBokun = async () => {
    setDiagnosticando(true);
    try {
      const { data } = await api.get("/reservas/diagnostico-barco-bokun");
      const conSenal = data.con_senal_de_barco_en_texto_guardado || [];
      if (conSenal.length > 0) {
        toast.warning(`${conSenal.length} reserva(s) de Bókun traen señal de barco y siguen en SIN BARCO: ${conSenal.slice(0, 5).join(", ")}. Corré "Reprocesar barco de Niágara".`);
      } else {
        toast.success(
          `${data.total_bokun_niagara_sin_barco} Niágara de Bókun en SIN BARCO · ${data.sin_fecha_guardada} sin fecha guardada (por eso no aparecen en la API, que no busca por código) · ${data.sin_rate_guardado} sin rate. Ninguna trae señal de barco en el texto guardado.`,
          { duration: 12000 });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDiagnosticando(false);
    }
  };

  const [reprocesandoBarco, setReprocesandoBarco] = useState(false);
  const reprocesarVarianteBarco = async () => {
    setReprocesandoBarco(true);
    try {
      await api.post("/reservas/reprocesar-variante-barco");
      toast.info("Reproceso de barco en curso. Relee los rates en Bókun; puede tardar unos minutos.");
      const inicio = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/reservas/reprocesar-variante-barco/estado");
          if (data.estado === "en_progreso" && Date.now() - inicio < 10 * 60 * 1000) return;
          clearInterval(poll);
          setReprocesandoBarco(false);
          if (data.estado === "error") {
            toast.error(`El reproceso falló: ${data.error}`);
            return;
          }
          const res = data.resultado || {};
          const r = res.resumen || {};
          const porCanal = r.corregidas_por_canal;
          const desglose = porCanal ? ` — Bókun: ${porCanal.bokun}, otros canales: ${porCanal.otros}` : "";
          const noEnc = r.bokun_no_encontradas_en_api
            ? ` · ${r.bokun_no_encontradas_en_api} de Bókun no aparecieron en la API (la mayoría sin fecha guardada).`
            : "";
          if (res.corregidas > 0) {
            toast.success(`${res.corregidas} reserva(s) de Niágara pasaron a CON BARCO sobre ${res.revisadas} revisada(s).${desglose}${noEnc}`);
          } else {
            toast.info(`Se revisaron ${res.revisadas} reserva(s) de Niágara y ninguna trae señal de barco. Sin cambios.${noEnc}`);
          }
          load();
        } catch {
          clearInterval(poll);
          setReprocesandoBarco(false);
        }
      }, 4000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setReprocesandoBarco(false);
    }
  };

  const togglePrueba = async (r) => {
    setTogglingId(r.id);
    try {
      await api.post(`/reservas/${r.id}/marcar-prueba`, { es_prueba: !r.es_prueba });
      toast.success(r.es_prueba ? "Reserva restaurada a la lista real." : "Reserva marcada como prueba: se oculta de la lista real.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="reservas-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <p className="text-sm text-slate-500">
          Reservas de todos los canales. Los servicios privados se listan en su propia pestaña.
        </p>
        <div className="flex gap-2">
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="herramientas-admin-button" className="gap-2">
                  <Wrench className="h-4 w-4" /> Herramientas <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem
                  onClick={reclasificarVip}
                  disabled={reclasificando}
                  data-testid="reclasificar-vip-button"
                  className="gap-2"
                >
                  {reclasificando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Reclasificar VIP</div>
                    <div className="text-xs text-slate-500">Privado que en realidad es VIP (grupo reducido)</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={reprocesarMapeoBokun}
                  disabled={reprocesandoBokun}
                  data-testid="reprocesar-mapeo-bokun-button"
                  className="gap-2"
                >
                  {reprocesandoBokun ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Reprocesar Bandeja Bókun</div>
                    <div className="text-xs text-slate-500">Reintenta mapear productos de Bókun/Viator sin tour (ej. nombres en inglés)</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={corregirTourAgenciaVip}
                  disabled={corrigiendoVipAgencia}
                  data-testid="corregir-tour-agencia-vip-button"
                  className="gap-2"
                >
                  {corrigiendoVipAgencia ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Corregir tour VIP por agencia</div>
                    <div className="text-xs text-slate-500">Reservas que quedaron en "... VIP" solo porque el nombre de la agencia lo mencionaba</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={corregirCancelacionesDesfasadas}
                  disabled={corrigiendoCancelaciones}
                  data-testid="corregir-cancelaciones-desfasadas-button"
                  className="gap-2"
                >
                  {corrigiendoCancelaciones ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Corregir cancelaciones desfasadas</div>
                    <div className="text-xs text-slate-500">Solicitudes marcadas "aplicada" cuya reserva nunca cambió de estado</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setVencidasOpen(true)}
                  data-testid="ver-pendientes-vencidas-button-admin"
                  className="gap-2"
                >
                  <Wand2 className="h-4 w-4" />
                  <div>
                    <div>Ver pendientes vencidas</div>
                    <div className="text-xs text-slate-500">Pendientes cuya fecha de tour ya pasó, separadas del backlog futuro</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={limpiarDuplicadosCargaMasiva}
                  disabled={limpiandoDuplicados}
                  data-testid="limpiar-duplicados-carga-masiva-button"
                  className="gap-2"
                >
                  {limpiandoDuplicados ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Eliminar duplicados de carga masiva</div>
                    <div className="text-xs text-slate-500">Mismo pasajero+tour+fecha+hora cargado dos veces (re-subida de archivo); conserva la más antigua</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLimpiezaOpen(true)}
                  data-testid="revisar-duplicados-button"
                  className="gap-2"
                >
                  <Wand2 className="h-4 w-4" />
                  <div>
                    <div>Revisar duplicados y datos de prueba</div>
                    <div className="text-xs text-slate-500">Mismo pasajero+tour+fecha en cualquier canal (aunque cambie el horario) y registros TEST/QA sembrados</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={reprocesarVarianteBarco}
                  disabled={reprocesandoBarco}
                  data-testid="reprocesar-variante-barco-button"
                  className="gap-2"
                >
                  {reprocesandoBarco ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Reprocesar barco de Niágara</div>
                    <div className="text-xs text-slate-500">Relee el rate real en Bókun (ej. "PASEO DE BARCO INCLUIDO") y corrige las que quedaron sin barco</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={diagnosticoBarcoBokun}
                  disabled={diagnosticando}
                  data-testid="diagnostico-barco-bokun-button"
                  className="gap-2"
                >
                  {diagnosticando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  <div>
                    <div>Diagnóstico barco Bókun</div>
                    <div className="text-xs text-slate-500">Qué son las Niágara de Bókun que la API no encuentra y si alguna lleva barco (solo lectura)</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setCuposOpen(true)}
                  data-testid="revisar-cupos-button"
                  className="gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <div>
                    <div>Revisar cupos de salidas</div>
                    <div className="text-xs text-slate-500">Compara el cupo usado de cada salida con los pasajeros reales de sus reservas y lo corrige</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isAdmin && (
            <Button
              variant="outline"
              onClick={() => setVencidasOpen(true)}
              data-testid="ver-pendientes-vencidas-button"
              className="gap-2"
            >
              <Wand2 className="h-4 w-4" /> Pendientes vencidas
            </Button>
          )}
          <Button variant="outline" onClick={() => setIngestOpen(true)} data-testid="paste-email-button" className="gap-2">
            <MailPlus className="h-4 w-4" /> Pegar correo
          </Button>
          <Button
            variant="outline"
            onClick={() => setSinHorarioOpen(true)}
            data-testid="reservas-sin-horario-button"
            className={`gap-2 ${sinHorarioCount > 0 ? "border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" : ""}`}
          >
            <Clock className="h-4 w-4" /> Sin horario{sinHorarioCount > 0 ? ` · ${sinHorarioCount}` : ""}
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="carga-masiva-button" className="gap-2">
            <UploadCloud className="h-4 w-4" /> Carga masiva
          </Button>
          <Button onClick={openNew} data-testid="new-reserva-button" className="gap-2">
            <Plus className="h-4 w-4" /> Cargar reserva
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar código, nombre, email, teléfono..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="reservas-search-input"
            />
          </div>
          <Select value={tourFilter} onValueChange={setTourFilter}>
            <SelectTrigger className="w-full sm:w-52" data-testid="reservas-tour-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los tours</SelectItem>
              {tours.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={horaFilter} onValueChange={setHoraFilter}>
            <SelectTrigger className="w-full sm:w-36" data-testid="reservas-hora-filter">
              <SelectValue placeholder="Toda hora" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Toda hora</SelectItem>
              {horasDisponibles.map((h) => (
                <SelectItem key={h} value={h}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="w-full sm:w-44" data-testid="reservas-estado-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estados</SelectItem>
              {Object.entries(ESTADOS_RESERVA).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={modalidadFilter} onValueChange={setModalidadFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="reservas-modalidad-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Toda modalidad</SelectItem>
              {Object.entries(TIPOS_MODALIDAD).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={canal} onValueChange={setCanal}>
            <SelectTrigger className="w-full sm:w-44" data-testid="reservas-canal-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los canales</SelectItem>
              <SelectItem value="getyourguide">GetYourGuide</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="correo">Correo</SelectItem>
              <SelectItem value="bokun">Bokun</SelectItem>
            </SelectContent>
          </Select>
          <Select value={agenciaFilter} onValueChange={setAgenciaFilter}>
            <SelectTrigger className="w-full sm:w-52" data-testid="reservas-agencia-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas las agencias</SelectItem>
              {agencias.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs text-slate-500">Rango de fechas:</span>
          <Input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="w-auto"
            data-testid="reservas-desde-filter"
          />
          <span className="text-slate-400">a</span>
          <Input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="w-auto"
            data-testid="reservas-hasta-filter"
          />
          {isAdmin && (
            <label className="flex items-center gap-1.5 ml-2 text-xs text-slate-500 cursor-pointer">
              <Checkbox
                checked={mostrarPrueba}
                onCheckedChange={(v) => setMostrarPrueba(!!v)}
                data-testid="reservas-mostrar-prueba-checkbox"
              />
              Mostrar datos de prueba
            </label>
          )}
          {(desde || hasta || tourFilter !== "todos" || horaFilter !== "todos" || modalidadFilter !== "todos" || estado !== "todos" || canal !== "todos" || agenciaFilter !== "todos") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDesde("");
                setHasta("");
                setTourFilter("todos");
                setHoraFilter("todos");
                setModalidadFilter("todos");
                setEstado("todos");
                setCanal("todos");
                setAgenciaFilter("todos");
                setQ("");
              }}
              className="text-slate-500"
              data-testid="reservas-limpiar-filtros"
            >
              Limpiar filtros
            </Button>
          )}
        </div>
      </div>

      {/* PROMPT 109: la lista trae como mucho 2000. Si hay mas, se dice. */}
      {totalReservas !== null && totalReservas > reservas.length && (
        <p
          className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
          data-testid="reservas-aviso-tope"
        >
          Se muestran las {reservas.length.toLocaleString("es")} cargadas más recientemente de{" "}
          {totalReservas.toLocaleString("es")} que coinciden. Para ver el resto, acotá el rango de fechas o usá los
          filtros o el buscador.
        </p>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="regular" data-testid="tab-regulares" className="gap-2">
            <Ticket className="h-4 w-4" /> Regulares
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">{regularesPorHora.length}</span>
          </TabsTrigger>
          <TabsTrigger value="privado" data-testid="tab-privados" className="gap-2">
            <Crown className="h-4 w-4" /> Privados
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">{privadosPorHora.length}</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="regular" className="mt-4">
          <TablaReservas
            rows={regularesPorHora}
            tours={tours}
            loading={loading}
            onEdit={openEdit}
            isAdmin={isAdmin}
            onTogglePrueba={togglePrueba}
            togglingId={togglingId}
          />
        </TabsContent>
        <TabsContent value="privado" className="mt-4">
          <p className="text-xs text-slate-500 mb-2">
            Las solicitudes de privados cargadas por agencias entran "Pendiente" (sin descontar cupo). Aceptalas o
            rechazalas según tengas vehículo disponible para esa fecha/horario.
          </p>
          <TablaReservas
            rows={privadosPorHora}
            tours={tours}
            loading={loading}
            onEdit={openEdit}
            onDecidir={decidirPrivado}
            decidiendoId={decidiendoId}
            isAdmin={isAdmin}
            onTogglePrueba={togglePrueba}
            togglingId={togglingId}
          />
        </TabsContent>
      </Tabs>

      <ReservaFormDialog open={formOpen} onOpenChange={setFormOpen} tours={tours} agencias={agencias} reserva={editing} onSaved={load} />
      <IngestEmailDialog open={ingestOpen} onOpenChange={setIngestOpen} onProcessed={load} />
      <ImportarReservasDialog open={importOpen} onOpenChange={setImportOpen} basePath="/reservas" onImported={load} />
      <PendientesVencidasDialog open={vencidasOpen} onOpenChange={setVencidasOpen} onChanged={load} />
      <LimpiezaDatosDialog open={limpiezaOpen} onOpenChange={setLimpiezaOpen} onDone={load} />
      <AsignarHorarioDialog
        open={sinHorarioOpen}
        onOpenChange={setSinHorarioOpen}
        isAdmin={isAdmin}
        onDone={() => { load(); loadSinHorario(); }}
      />
      <CuposDesincronizadosDialog open={cuposOpen} onOpenChange={setCuposOpen} onDone={load} />
    </div>
  );
}
